/** packages/shared-components/src/components/Keyboard/Key.tsx */

import React, { useState, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import type { KeyConfig, KeyState } from "./types";
import { HigherKeyMode } from "@eweadn-qiankun/shared-types";
import {
  resolveKeyboardKeyIcon,
  resolveKeyboardKeyTextLabel,
} from "./iconConfig";
import { Iconfont } from "../Iconfont";
import { Tooltip } from "../Tooltip";
import type { TooltipPlacement } from "../Tooltip/types";
import type { AdcRouteStatusDataItem } from "@eweadn-qiankun/shared-types"; // 引入必要类型

/** 预估气泡高度 + portal 间距（portalGapPx=12），略小于实际以免误判朝下 */
const EXTRA_KEY_TOOLTIP_GAP = 40;
const KEY_REMAP_SCROLL_SELECTOR = ".keyboard-layout-selector-content";

/** Portal Tooltip 锚定到键帽按钮，避免包裹层在 flex 布局下尺寸偏移 */
const resolveKeyboardKeyTooltipAnchor = (wrapper: HTMLElement) =>
  wrapper.querySelector<HTMLElement>(".keyboard-button") ?? wrapper;

/**
 * 按改键面板滚动区「可视区域」判断 Tooltip 朝上/朝下（默认朝上）。
 * 仅看按键距滚动容器当前可视顶部的距离，不因上一分组在文档流中更高而强制朝下。
 */
function resolveExtraKeyTooltipPlacement(el: HTMLElement): TooltipPlacement {
  const rect = el.getBoundingClientRect();
  const scrollRoot = el.closest(
    KEY_REMAP_SCROLL_SELECTOR
  ) as HTMLElement | null;
  const scrollBounds = scrollRoot?.getBoundingClientRect() ?? {
    top: 0,
    bottom: window.innerHeight,
  };

  const clearanceAbove = rect.top - scrollBounds.top;
  const spaceBelow = scrollBounds.bottom - rect.bottom;

  if (clearanceAbove >= EXTRA_KEY_TOOLTIP_GAP) return "top";
  if (spaceBelow >= EXTRA_KEY_TOOLTIP_GAP) return "bottom";
  return spaceBelow >= clearanceAbove ? "bottom" : "top";
}

export interface KeyProps {
  /** 按键配置 */
  config: KeyConfig;
  keySize: number;
  /** 按键状态 */
  state: KeyState;
  /** 是否选中 */
  isSelected: boolean;
  /** 自定义颜色 */
  customColor?: string;
  /** 双灯位上灯颜色 */
  customColorTop?: string;
  /** 双灯位下灯颜色 */
  customColorBottom?: string;
  /** 以上下两半呈现灯色（双灯位） */
  doubleLightingSplit?: boolean;
  /** 双灯位分半层：dual 双灯 / upper 仅上 / lower 仅下 */
  doubleLightingSplitLayer?: "dual" | "upper" | "lower";
  /** 样式模式 */
  styleMode: "colorful" | "neutral";
  /** 灯光模式 */
  lightingMode: "default" | "custom" | "off";
  /** 是否显示标签 */
  showLabels: boolean;
  /** 高级键模式（可选） */
  advancedKeyMode?: HigherKeyMode;
  /** 点击回调 */
  onClick: (keyCode: string, config: KeyConfig, e: React.MouseEvent) => void;
  /** 鼠标左键按下按键（用于滑动选中） */
  onMouseDownSelect?: (
    keyCode: string,
    config: KeyConfig,
    e: React.MouseEvent
  ) => void;
  /** 鼠标按住左键滑过按键（用于滑动选中） */
  onMouseEnterSelect?: (
    keyCode: string,
    config: KeyConfig,
    e: React.MouseEvent
  ) => void;
  /** 右键菜单回调（需自行 preventDefault 以屏蔽系统菜单） */
  onContextMenu?: (
    keyCode: string,
    config: KeyConfig,
    e: React.MouseEvent
  ) => void;
  /** 双击回调（如星闪自定义灯清除单键颜色） */
  onDoubleClick?: (
    keyCode: string,
    config: KeyConfig,
    e: React.MouseEvent
  ) => void;
  /** 拖拽目标回调（接收目标 KeyCode） */
  onDrop?: (
    sourceKeyCode: number,
    targetRow: number,
    targetCol: number
  ) => void;
  /** 是否禁用按键点击 */
  disabled?: boolean;
  /** 是否可拖拽 */
  draggable?: boolean;
  isExtraKeys?: boolean;
  /** 扩展键悬停文案格式：labelWithComm = 原生 title「标签（i18n 说明）」 */
  extraKeyTooltipFormat?: "comm" | "labelWithComm";
  /** 自定义按下状态样式 */
  pressedStyle?: React.CSSProperties;
  /** 自定义选中状态样式 */
  selectedStyle?: React.CSSProperties;
  /** 选中态是否保留实时轮询颜色 */
  keepRealtimeColorWhenSelected?: boolean;
  /** 自定义灯色按键右上角标识图 URL（由 Keyboard 传入） */
  customColorCornerIconSrc?: string;
  /** 是否在该键上展示右上角标识 */
  showCustomColorCornerIcon?: boolean;
  /** 按键被选中时显示的 badge 文字（用于配置中展示当前 mode） */
  selectedKeyBadge?: string;
  /** 按键被选中时常驻显示的 Tooltip 文案 */
  selectedKeyTooltipText?: React.ReactNode;
  /** 选中态下子元素样式覆盖 */
  selectedKeyStyleMap?: {
    label?: React.CSSProperties;
    badge?: React.CSSProperties;
  };
  /** 自定义灯色等场景：不套用默认选中黑/白字样式，仅保留键色与打勾 */
  suppressDefaultSelectedChrome?: boolean;
  /** 图标键选中时仍展示高亮（社区改键预览等） */
  allowIconKeySelectedStyle?: boolean;
  /** 随机值显示 */
  randomValue?: AdcRouteStatusDataItem;
  randomAdcValue?: any;
  randomRouteValue?: any;
  randomStatusValue?: any;
  randomPerformanceValue?: any;
  /** 是否显示随机值 */
  showRandomValue?: boolean;
  /** 隐藏校准 ADC / 行程数值 */
  hideCalibrationMetrics?: boolean;
  showTeampreset?: boolean;
  teampresetData?: any;
  axisBody?: any;
  showAxis?: boolean;
  axisData?: any;
  /** 混轴联动高亮（非选中态） */
  isAxisLinkHighlighted?: boolean;
  onAxisLinkHover?: () => void;
  onAxisLinkHoverEnd?: () => void;
}

/** 键帽行程数字：避免数组/脏数据被 React 连成「000…」 */
const formatKeyTravelMetric = (value: unknown): string => {
  if (value == null) return "";
  if (Array.isArray(value)) {
    const first = value.find((item) => Number.isFinite(Number(item)));
    return first == null ? "" : formatKeyTravelMetric(first);
  }
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const rounded = Math.round(n * 1000) / 1000;
  return String(rounded);
};

const getAdvancedKeyLabel = (mode: HigherKeyMode): string => {
  switch (mode) {
    case HigherKeyMode.DKS:
      return "DKS";
    case HigherKeyMode.MPT:
      return "MPT";
    case HigherKeyMode.MT:
      return "MT";
    case HigherKeyMode.TGL:
      return "TGL";
    case HigherKeyMode.END:
      return "END";
    case HigherKeyMode.SOCD:
      return "SOCD";
    case HigherKeyMode.RS:
      return "RS";
    case HigherKeyMode.CB:
      return "CB";
    case HigherKeyMode.SNAPKEY:
      return "SnapKey";
    case HigherKeyMode.MDS:
      return "MDS";
    default:
      return "";
  }
};

export const Key = forwardRef<HTMLButtonElement, KeyProps>(
  (
    {
      config,
      keySize,
      state,
      isSelected,
      customColor,
      customColorTop,
      customColorBottom,
      doubleLightingSplit = false,
      doubleLightingSplitLayer = "dual",
      lightingMode,
      showLabels,
      isExtraKeys,
      extraKeyTooltipFormat = "comm",
      advancedKeyMode,
      onClick,
      onMouseDownSelect,
      onMouseEnterSelect,
      onContextMenu,
      onDoubleClick,
      onDrop,
      disabled = false,
      draggable = false,
      pressedStyle,
      selectedStyle,
      keepRealtimeColorWhenSelected = false,
      randomAdcValue,
      randomRouteValue,
      randomStatusValue,
      randomPerformanceValue,
      showRandomValue,
      hideCalibrationMetrics = false,
      showTeampreset = false,
      teampresetData = [],
      axisBody,
      showAxis,
      axisData,
      isAxisLinkHighlighted,
      onAxisLinkHover,
      onAxisLinkHoverEnd,
      customColorCornerIconSrc,
      showCustomColorCornerIcon,
      selectedKeyBadge,
      selectedKeyTooltipText,
      selectedKeyStyleMap,
      suppressDefaultSelectedChrome = false,
      allowIconKeySelectedStyle = false,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [isDragOver, setIsDragOver] = useState(false);
    const [extraKeyTooltipPlacement, setExtraKeyTooltipPlacement] =
      useState<TooltipPlacement>("top");

    const translateComm = (comm: string): string => {
      switch (comm) {
        case "keyboard.keyFunction.media.brightnessUp":
          return t("keyboard.keyFunction.media.brightnessUp");
        case "keyboard.keyFunction.media.brightnessDown":
          return t("keyboard.keyFunction.media.brightnessDown");
        case "keyboard.keyFunction.media.nextTrack":
          return t("keyboard.keyFunction.media.nextTrack");
        case "keyboard.keyFunction.media.prevTrack":
          return t("keyboard.keyFunction.media.prevTrack");
        case "keyboard.keyFunction.media.stop":
          return t("keyboard.keyFunction.media.stop");
        case "keyboard.keyFunction.media.playPause":
          return t("keyboard.keyFunction.media.playPause");
        case "keyboard.keyFunction.media.systemMultimedia":
          return t("keyboard.keyFunction.media.systemMultimedia");
        case "keyboard.keyFunction.media.pauseMedia":
          return t("keyboard.keyFunction.media.pauseMedia");
        case "keyboard.keyFunction.media.mute":
          return t("keyboard.keyFunction.media.mute");
        case "keyboard.keyFunction.media.volumeUp":
          return t("keyboard.keyFunction.media.volumeUp");
        case "keyboard.keyFunction.media.volumeDown":
          return t("keyboard.keyFunction.media.volumeDown");
        case "keyboard.keyFunction.media.music":
          return t("keyboard.keyFunction.media.music");
        case "keyboard.keyFunction.media.calculator":
          return t("keyboard.keyFunction.media.calculator");
        case "keyboard.keyFunction.media.mail":
          return t("keyboard.keyFunction.media.mail");
        case "keyboard.keyFunction.media.micMute":
          return t("keyboard.keyFunction.media.micMute");
        case "keyboard.keyFunction.media.email":
          return t("keyboard.keyFunction.media.email");
        case "keyboard.keyFunction.media.favorites":
          return t("keyboard.keyFunction.media.favorites");
        case "keyboard.keyFunction.media.appSwitch":
          return t("keyboard.keyFunction.media.appSwitch");
        case "keyboard.keyFunction.media.myComputer":
          return t("keyboard.keyFunction.media.myComputer");
        case "keyboard.keyFunction.media.browser":
          return t("keyboard.keyFunction.media.browser");
        case "keyboard.keyFunction.mouse.release":
          return t("keyboard.keyFunction.mouse.release");
        case "keyboard.keyFunction.mouse.leftClick":
          return t("keyboard.keyFunction.mouse.leftClick");
        case "keyboard.keyFunction.mouse.rightClick":
          return t("keyboard.keyFunction.mouse.rightClick");
        case "keyboard.keyFunction.mouse.middleClick":
          return t("keyboard.keyFunction.mouse.middleClick");
        case "keyboard.keyFunction.mouse.moveLeft":
          return t("keyboard.keyFunction.mouse.moveLeft");
        case "keyboard.keyFunction.mouse.moveRight":
          return t("keyboard.keyFunction.mouse.moveRight");
        case "keyboard.keyFunction.mouse.moveUp":
          return t("keyboard.keyFunction.mouse.moveUp");
        case "keyboard.keyFunction.mouse.moveDown":
          return t("keyboard.keyFunction.mouse.moveDown");
        case "keyboard.keyFunction.mouse.wheelForward":
          return t("keyboard.keyFunction.mouse.wheelForward");
        case "keyboard.keyFunction.mouse.wheelBackward":
          return t("keyboard.keyFunction.mouse.wheelBackward");
        case "keyboard.keyFunction.control.factoryReset":
          return t("keyboard.keyFunction.control.factoryReset");
        case "keyboard.keyFunction.control.switchToWin":
          return t("keyboard.keyFunction.control.switchToWin");
        case "keyboard.keyFunction.control.switchToMac":
          return t("keyboard.keyFunction.control.switchToMac");
        case "keyboard.keyFunction.control.calibrate":
          return t("keyboard.keyFunction.control.calibrate");
        case "keyboard.keyFunction.control.config1":
          return t("keyboard.keyFunction.control.config1");
        case "keyboard.keyFunction.control.config2":
          return t("keyboard.keyFunction.control.config2");
        case "keyboard.keyFunction.control.config3":
          return t("keyboard.keyFunction.control.config3");
        case "keyboard.keyFunction.control.config4":
          return t("keyboard.keyFunction.control.config4");
        case "keyboard.keyFunction.control.lockWin":
          return t("keyboard.keyFunction.control.lockWin");
        case "keyboard.keyFunction.light.modeSwitch":
          return t("keyboard.keyFunction.light.modeSwitch");
        case "keyboard.keyFunction.light.colorSwitch":
          return t("keyboard.keyFunction.light.colorSwitch");
        case "keyboard.keyFunction.light.brightnessUp":
          return t("keyboard.keyFunction.light.brightnessUp");
        case "keyboard.keyFunction.light.brightnessDown":
          return t("keyboard.keyFunction.light.brightnessDown");
        case "keyboard.keyFunction.light.speedUp":
          return t("keyboard.keyFunction.light.speedUp");
        case "keyboard.keyFunction.light.speedDown":
          return t("keyboard.keyFunction.light.speedDown");
        case "keyboard.keyFunction.light.prevMode":
          return t("keyboard.keyFunction.light.prevMode");
        case "keyboard.keyFunction.light.onOff":
          return t("keyboard.keyFunction.light.onOff");
        case "keyboard.keyFunction.light.direction":
          return t("keyboard.keyFunction.light.direction");
        case "keyboard.keyFunction.light.deco1ModeSwitch":
          return t("keyboard.keyFunction.light.deco1ModeSwitch");
        case "keyboard.keyFunction.light.deco1ColorSwitch":
          return t("keyboard.keyFunction.light.deco1ColorSwitch");
        case "keyboard.keyFunction.light.deco1BrightnessUp":
          return t("keyboard.keyFunction.light.deco1BrightnessUp");
        case "keyboard.keyFunction.light.deco1BrightnessDown":
          return t("keyboard.keyFunction.light.deco1BrightnessDown");
        case "keyboard.keyFunction.light.deco1SpeedUp":
          return t("keyboard.keyFunction.light.deco1SpeedUp");
        case "keyboard.keyFunction.light.deco1SpeedDown":
          return t("keyboard.keyFunction.light.deco1SpeedDown");
        case "keyboard.keyFunction.light.deco1PrevMode":
          return t("keyboard.keyFunction.light.deco1PrevMode");
        case "keyboard.keyFunction.light.deco1OnOff":
          return t("keyboard.keyFunction.light.deco1OnOff");
        case "keyboard.keyFunction.light.deco1Direction":
          return t("keyboard.keyFunction.light.deco1Direction");
        case "keyboard.keyFunction.light.deco2ModeSwitch":
          return t("keyboard.keyFunction.light.deco2ModeSwitch");
        case "keyboard.keyFunction.light.deco2ColorSwitch":
          return t("keyboard.keyFunction.light.deco2ColorSwitch");
        case "keyboard.keyFunction.light.deco2BrightnessUp":
          return t("keyboard.keyFunction.light.deco2BrightnessUp");
        case "keyboard.keyFunction.light.deco2BrightnessDown":
          return t("keyboard.keyFunction.light.deco2BrightnessDown");
        case "keyboard.keyFunction.light.deco2SpeedUp":
          return t("keyboard.keyFunction.light.deco2SpeedUp");
        case "keyboard.keyFunction.light.deco2SpeedDown":
          return t("keyboard.keyFunction.light.deco2SpeedDown");
        case "keyboard.keyFunction.light.deco2PrevMode":
          return t("keyboard.keyFunction.light.deco2PrevMode");
        case "keyboard.keyFunction.light.deco2OnOff":
          return t("keyboard.keyFunction.light.deco2OnOff");
        case "keyboard.keyFunction.light.deco2Direction":
          return t("keyboard.keyFunction.light.deco2Direction");
        case "keyboard.keyFunction.light.deco3ModeSwitch":
          return t("keyboard.keyFunction.light.deco3ModeSwitch");
        case "keyboard.keyFunction.light.deco3ColorSwitch":
          return t("keyboard.keyFunction.light.deco3ColorSwitch");
        case "keyboard.keyFunction.light.deco3BrightnessUp":
          return t("keyboard.keyFunction.light.deco3BrightnessUp");
        case "keyboard.keyFunction.light.deco3BrightnessDown":
          return t("keyboard.keyFunction.light.deco3BrightnessDown");
        case "keyboard.keyFunction.light.deco3SpeedUp":
          return t("keyboard.keyFunction.light.deco3SpeedUp");
        case "keyboard.keyFunction.light.deco3SpeedDown":
          return t("keyboard.keyFunction.light.deco3SpeedDown");
        case "keyboard.keyFunction.light.deco3PrevMode":
          return t("keyboard.keyFunction.light.deco3PrevMode");
        case "keyboard.keyFunction.light.deco3OnOff":
          return t("keyboard.keyFunction.light.deco3OnOff");
        case "keyboard.keyFunction.light.deco3Direction":
          return t("keyboard.keyFunction.light.deco3Direction");
        default:
          /* 宏按键等未在 switch 中的 i18n key 也参与翻译，用于悬浮文案 */
          return typeof comm === "string" && comm.startsWith("keyboard.")
            ? t(comm)
            : comm;
      }
    };

    // 处理拖拽开始
    const handleDragStart = (e: React.DragEvent) => {
      e.dataTransfer.setData("keyCode", config.keyCode.toString());
      e.dataTransfer.effectAllowed = "move";
    };

    // 处理拖拽进入
    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };

    // 处理拖拽离开
    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    };

    // 处理放置
    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const sourceKeyCode = e.dataTransfer.getData("keyCode");
      if (
        !sourceKeyCode ||
        config.originalRow === undefined ||
        config.originalCol === undefined
      ) {
        return;
      }

      onDrop?.(
        parseInt(sourceKeyCode, 10),
        config.originalRow,
        config.originalCol
      );
    };

    const hasAdvancedKey =
      advancedKeyMode !== undefined && advancedKeyMode !== HigherKeyMode.NONE;
    // 图标键默认不展示普通「选中态」（改键页点选时不抢视觉）；社区预览可显式开启
    const iconName = resolveKeyboardKeyIcon(config.keyCode);
    const iconExists = Boolean(iconName);
    const textLabel = resolveKeyboardKeyTextLabel(config.keyCode, config.label);
    const showTextLabel = showLabels && !iconExists && Boolean(textLabel);
    const allowSelectedVisual = allowIconKeySelectedStyle || !iconExists;
    const calibrationHighlighted =
      showRandomValue &&
      typeof selectedStyle?.backgroundColor === "string" &&
      selectedStyle.backgroundColor.length > 0;
    const macroKeyCodeMin = 62720;
    const macroKeyCodeMax = 62739;
    const isMacroKey =
      config.keyCode >= macroKeyCodeMin && config.keyCode <= macroKeyCodeMax;
    const isMacroUnconfiguredDisabled = isExtraKeys && isMacroKey && disabled;

    const showDefaultSelectedChrome =
      !suppressDefaultSelectedChrome &&
      (calibrationHighlighted ||
        (allowSelectedVisual &&
          (hideCalibrationMetrics
            ? randomStatusValue === 2 || randomStatusValue === 1
            : isSelected || randomStatusValue === 2)));

    const allowPhysicalPressedVisual =
      state.pressed &&
      (hideCalibrationMetrics ? showRandomValue : !showRandomValue);

    /** 双灯位：上下两半独立填色（上/下灯位 Tab 仅对应半区亮） */
    const useDualLightingSplit =
      doubleLightingSplit && lightingMode === "custom";

    const keyClassName = [
      "shared-keyboard-key",
      allowPhysicalPressedVisual && "shared-keyboard-key-pressed",
      showDefaultSelectedChrome && "shared-keyboard-key-selected",
      lightingMode === "custom" &&
        !useDualLightingSplit &&
        customColor &&
        "shared-keyboard-key-custom",
      useDualLightingSplit && "shared-keyboard-key-dual-split",
      useDualLightingSplit &&
        doubleLightingSplitLayer === "dual" &&
        "shared-keyboard-key-dual-split-dual",
      useDualLightingSplit &&
        doubleLightingSplitLayer === "upper" &&
        "shared-keyboard-key-dual-split-upper",
      useDualLightingSplit &&
        doubleLightingSplitLayer === "lower" &&
        "shared-keyboard-key-dual-split-lower",
      isDragOver && "shared-keyboard-key-drag-over",
      hasAdvancedKey && "shared-keyboard-key-advanced",
      isMacroUnconfiguredDisabled && "shared-keyboard-key-macro-disabled",
      isAxisLinkHighlighted && "shared-keyboard-key-axis-link-highlight",
    ]
      .filter(Boolean)
      .join(" ");

    const hasSelectedVisualState =
      calibrationHighlighted ||
      (allowSelectedVisual &&
        (hideCalibrationMetrics
          ? randomStatusValue === 2 || randomStatusValue === 1
          : isSelected || randomStatusValue === 2 || randomStatusValue === 1));

    /**
     * 自定义灯：
     * - 已设色（含选中后改红）：始终 inline 铺色
     * - 仅选中未设色：用主题底；勿 transparent（灯光页容器下会显黑）
     */
    let customLightingBackground: string | undefined;
    if (lightingMode === "custom" && !useDualLightingSplit) {
      if (customColor) {
        customLightingBackground = customColor;
      } else if (
        hasSelectedVisualState &&
        (keepRealtimeColorWhenSelected || suppressDefaultSelectedChrome)
      ) {
        customLightingBackground = "var(--keyboard-bg)";
      }
    }

    const verticalSpan =
      config.verticalSpan ?? ([87, 88].includes(config.keyCode) ? 2 : 1);
    const style: React.CSSProperties = {
      width: `calc(${config.multipleWidth || 1} * var(--key-size))`,
      height: `${verticalSpan > 1 ? 2.1 * keySize : keySize}px`,
      ...(verticalSpan > 1 ? { zIndex: 1 } : {}),
      ...(allowPhysicalPressedVisual ? pressedStyle : {}),
      ...(showDefaultSelectedChrome ? selectedStyle : {}),
      ...(customLightingBackground !== undefined
        ? { background: customLightingBackground }
        : {}),
    };

    const getRandomHeight = () => {
      if (!showRandomValue || randomRouteValue == null) {
        return "0%";
      }
      const route = Number(randomRouteValue);
      if (!Number.isFinite(route) || route <= 0) {
        return "0%";
      }
      const rawDrive =
        axisBody?.drive_range ??
        randomPerformanceValue?.axisRangeMax ??
        axisBody?.axisRangeMax ??
        axisBody?.axis_range_max;
      const driveNum = Number(rawDrive);
      if (!Number.isFinite(driveNum) || driveNum <= 0) {
        return "0%";
      }
      /** v2 为 mm（如 3.5）；v1/HTTP 为 µm 字符串（如 3500） */
      const driveMicrons = driveNum < 100 ? driveNum * 1000 : driveNum;
      return `${Math.min(100, (route / driveMicrons) * 100)}%`;
    };

    const extraKeyCommText = translateComm(config.comm);
    const nativeKeyTitle =
      extraKeyTooltipFormat === "labelWithComm"
        ? config.label && extraKeyCommText
          ? `${config.label}（${extraKeyCommText}）`
          : config.label
        : isExtraKeys
          ? undefined
          : config.label;
    const extraKeyTooltipText =
      isExtraKeys &&
      extraKeyTooltipFormat !== "labelWithComm" &&
      (isMacroKey
        ? t("keyboard.keyFunction.macro.keyLabel", {
            index: config.keyCode - macroKeyCodeMin,
          })
        : extraKeyCommText || config.label || "");

    const keyButton = (
      <button
        ref={ref}
        className={`keyboard-button ${keyClassName}`}
        style={style}
        onClick={(e) => !disabled && onClick(config.uuid, config, e)}
        onDoubleClick={(e) => {
          if (disabled || !onDoubleClick) return;
          e.preventDefault();
          e.stopPropagation();
          onDoubleClick(config.uuid, config, e);
        }}
        onMouseDown={(e) => {
          if (disabled) return;
          onMouseDownSelect?.(config.uuid, config, e);
        }}
        onContextMenu={(e) => {
          if (disabled || !onContextMenu) return;
          onContextMenu(config.uuid, config, e);
        }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onMouseEnter={(e) => {
          if (!disabled) {
            onMouseEnterSelect?.(config.uuid, config, e);
            onAxisLinkHover?.();
          }
          if (extraKeyTooltipText) {
            setExtraKeyTooltipPlacement(
              resolveExtraKeyTooltipPlacement(e.currentTarget)
            );
          }
        }}
        onMouseLeave={() => {
          onAxisLinkHoverEnd?.();
        }}
        draggable={draggable}
        disabled={disabled}
        data-code={config.code}
        title={nativeKeyTitle}
      >
        {useDualLightingSplit ? (
          <div className="shared-keyboard-key-dual-layers" aria-hidden>
            <div
              className="shared-keyboard-key-dual-half shared-keyboard-key-dual-half-top"
              style={{
                background: customColorTop ?? "var(--bg-tertiary)",
              }}
            />
            <div
              className="shared-keyboard-key-dual-half shared-keyboard-key-dual-half-bottom"
              style={{
                background: customColorBottom ?? "var(--bg-tertiary)",
              }}
            />
          </div>
        ) : null}
        <div className="shared-keyboard-key-content">
          {iconExists ? (
            <div className="shared-keyboard-key-icon">
              <Iconfont icon={iconName} />
            </div>
          ) : (
            showTextLabel && (
              <span
                className="shared-keyboard-key-label"
                style={
                  showDefaultSelectedChrome
                    ? selectedKeyStyleMap?.label
                    : undefined
                }
              >
                {textLabel}
              </span>
            )
          )}
          {showRandomValue ? (
            <div className="calibration-box">
              {!hideCalibrationMetrics ? (
                <>
                  <div className="calibration-text">
                    {randomAdcValue}
                    {/* {randomRouteValue} */}
                  </div>
                  <div
                    className="calibration-bg"
                    style={{
                      height: getRandomHeight(),
                    }}
                  ></div>
                  <div className="calibration-number">
                    {(Number(randomRouteValue) / 1000).toFixed(2)}mm
                  </div>
                </>
              ) : null}
            </div>
          ) : showTeampreset ? (
            <div className="teampreset-box">
              <div className="teampreset-first">
                {formatKeyTravelMetric(
                  teampresetData?.mode === 0
                    ? teampresetData?.normalRelease
                    : teampresetData?.rtFirstTouch
                )}
              </div>
              {teampresetData?.mode !== 0 && (
                <div className="teampreset-dwon-up">
                  <div className="teampreset-down">
                    {formatKeyTravelMetric(teampresetData?.rtPress)}
                  </div>
                  <div className="teampreset-up">
                    {formatKeyTravelMetric(teampresetData?.rtRelease)}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          {(hasAdvancedKey || (isSelected && selectedKeyBadge)) && (
            <div
              className="shared-keyboard-key-badge"
              style={
                showDefaultSelectedChrome
                  ? selectedKeyStyleMap?.badge
                  : undefined
              }
            >
              {hasAdvancedKey
                ? getAdvancedKeyLabel(advancedKeyMode)
                : selectedKeyBadge}
            </div>
          )}
          {showAxis ? (
            <div
              className={
                isAxisLinkHighlighted
                  ? "axis-box axis-box-link-highlight"
                  : "axis-box"
              }
              title={axisData?.axis_name}
            >
              {/* {axisData?.axis_name} */}
              {axisData?.aixsDetail &&
              axisData?.aixsDetail.length > 0 &&
              axisData?.aixsDetail[0]?.axis_id === 4128
                ? "磁玉Pro 超竞版"
                : (axisData?.axis_name ?? "")}
            </div>
          ) : null}
        </div>
        {lightingMode === "custom" &&
          customColorCornerIconSrc &&
          showCustomColorCornerIcon && (
            <img
              src={customColorCornerIconSrc}
              alt=""
              className="shared-keyboard-key-custom-corner-icon"
              draggable={false}
            />
          )}
      </button>
    );

    const keyButtonWithSelectedTooltip = selectedKeyTooltipText ? (
      <Tooltip
        text={selectedKeyTooltipText}
        placement="top"
        trigger="always"
        usePortal
        portalGapPx={12}
        portalScrollParentSelector={KEY_REMAP_SCROLL_SELECTOR}
        getAnchorElement={resolveKeyboardKeyTooltipAnchor}
        className="shared-keyboard-key-tooltip-host"
      >
        {keyButton}
      </Tooltip>
    ) : (
      keyButton
    );

    return (
      <>
        {extraKeyTooltipText ? (
          <Tooltip
            text={extraKeyTooltipText}
            placement={extraKeyTooltipPlacement}
            usePortal
            portalScrollParentSelector={KEY_REMAP_SCROLL_SELECTOR}
            portalGapPx={12}
            maxWidth={280}
            getAnchorElement={resolveKeyboardKeyTooltipAnchor}
            className="shared-keyboard-key-tooltip-host"
            delay={50}
          >
            {keyButtonWithSelectedTooltip}
          </Tooltip>
        ) : (
          keyButtonWithSelectedTooltip
        )}
      </>
    );
  }
);

// 添加 display name，方便 React DevTools 调试
Key.displayName = "Key";
